import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import EnvironmentDetector from '../utils/EnvironmentDetector';
import useAdminAutoRefresh from './useAdminAutoRefresh';
import './AdminDashboard.css';
import './AdminUsers.css';
import './AdminSessions.css';
import './AdminPayments.css';

const AUTO_REFRESH_INTERVAL_MS = 15000;

const RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: 'all', label: 'All Time' },
];

const ENVIRONMENT_OPTIONS = [
  { value: 'production', label: 'Production' },
  { value: 'staging', label: 'Staging' },
  { value: 'all', label: 'All Environments' },
];

const tabs = [
  { to: '/admin/payments', label: 'Overview', key: 'overview', end: true },
  { to: '/admin/payments/wallets', label: 'Wallets', key: 'wallets' },
  { to: '/admin/payments/transactions', label: 'Credit Transactions', key: 'transactions' },
  { to: '/admin/payments/stripe-events', label: 'Payment Issues', key: 'issues' },
  { to: '/admin/payments/products', label: 'Products', key: 'products' },
  { to: '/admin/payments/stripe-payments', label: 'Stripe Payments', key: 'payments' },
];

const viewConfigs = {
  overview: {
    title: 'Payments & Credits',
    endpoint: null,
  },
  wallets: {
    title: 'Wallets',
    endpoint: '/.netlify/functions/admin-get-wallets',
  },
  transactions: {
    title: 'Credit Transactions',
    endpoint: '/.netlify/functions/admin-get-credit-transactions',
  },
  issues: {
    title: 'Payment Issues',
    endpoint: '/.netlify/functions/admin-get-payment-issues',
  },
  products: {
    title: 'Products & Credit Bundles',
    endpoint: '/.netlify/functions/admin-get-credit-products',
  },
  payments: {
    title: 'Stripe Payments',
    endpoint: '/.netlify/functions/admin-get-stripe-payments',
  },
};

function formatDateTime(value) {
  if (!value) {
    return 'Unknown';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatInteger(value) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value) || 0);
}

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function boolLabel(value) {
  return value ? 'Yes' : 'No';
}

function paymentBadgeClass(statusOrType) {
  if (['failed', 'needs_review', 'payment_status_issue', 'stripe_error'].includes(statusOrType)) {
    return 'cq-admin-badge cq-admin-badge--disabled';
  }
  if (['expired', 'refunded'].includes(statusOrType)) {
    return 'cq-admin-badge cq-admin-badge--warn';
  }
  if (statusOrType === 'completed' || statusOrType === 'active') {
    return 'cq-admin-badge cq-admin-badge--active';
  }
  return 'cq-admin-badge cq-admin-badge--neutral';
}

const SummaryCard = ({ title, value, helper }) => (
  <article className="cq-admin-metric-card">
    <p className="cq-admin-metric-card__title">{title}</p>
    <p className="cq-admin-metric-card__value">{value}</p>
    {helper ? <p className="cq-admin-metric-card__helper">{helper}</p> : null}
  </article>
);

function getDefaultEnvironment() {
  const detected = EnvironmentDetector.detectEnvironment();
  return ENVIRONMENT_OPTIONS.some((option) => option.value === detected) ? detected : 'production';
}

const AdminPaymentsPage = ({ view }) => {
  const config = viewConfigs[view];
  const token = localStorage.getItem('auth_token');
  const [range, setRange] = useState('7d');
  const [environment, setEnvironment] = useState(getDefaultEnvironment);
  const [summaryState, setSummaryState] = useState({
    loading: true,
    refreshing: false,
    error: '',
    summary: null,
    lastUpdatedAt: null,
  });
  const [listState, setListState] = useState({
    loading: view !== 'overview',
    refreshing: false,
    error: '',
    items: [],
    pagination: {
      page: 1,
      total_pages: 1,
      total_count: 0,
    },
  });
  const [filters, setFilters] = useState({
    query: '',
    status: '',
    internal_test: '',
    has_purchases: '',
    has_manual_adjustments: '',
    min_balance: '',
    max_balance: '',
    transaction_type: '',
    manual_only: '',
    has_game_session: '',
    has_stripe_payment: '',
    wallet_updated: '',
    webhook_received: '',
    product: '',
    issue_status: '',
    event_type: '',
    active: '',
  });
  const [flashMessage, setFlashMessage] = useState({ type: '', text: '' });
  const [reconcileState, setReconcileState] = useState({
    open: false,
    paymentId: '',
    label: '',
    note: '',
    canRerunCreditGrant: false,
    rerunCreditGrant: false,
    submitting: false,
    error: '',
  });
  const { refreshNonce, triggerRefresh } = useAdminAutoRefresh({ intervalMs: AUTO_REFRESH_INTERVAL_MS });

  useEffect(() => {
    const controller = new AbortController();

    const loadSummary = async ({ background = false } = {}) => {
      setSummaryState((current) => ({
        ...current,
        loading: background ? current.loading : true,
        refreshing: background,
        error: '',
      }));

      try {
        const response = await fetch('/.netlify/functions/admin-get-payments-summary', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ range, environment }),
          signal: controller.signal,
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.message || 'Unable to load payments summary.');
        }

        setSummaryState({
          loading: false,
          refreshing: false,
          error: '',
          summary: data.summary || null,
          lastUpdatedAt: new Date().toISOString(),
        });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setSummaryState({
          loading: false,
          refreshing: false,
          error: error.message || 'Unable to load payments summary.',
          summary: null,
          lastUpdatedAt: null,
        });
      }
    };

    loadSummary({ background: refreshNonce > 0 });

    return () => controller.abort();
  }, [environment, range, refreshNonce, token]);

  useEffect(() => {
    if (view === 'overview' || !config.endpoint) {
      setListState({
        loading: false,
        refreshing: false,
        error: '',
        items: [],
        pagination: {
          page: 1,
          total_pages: 1,
          total_count: 0,
        },
      });
      return undefined;
    }

    const controller = new AbortController();

    const loadList = async ({ background = false } = {}) => {
      setListState((current) => ({
        ...current,
        loading: background ? current.loading : true,
        refreshing: background,
        error: '',
      }));

      try {
        const response = await fetch(config.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...filters,
            range,
            environment,
            page: listState.pagination.page,
            page_size: 25,
          }),
          signal: controller.signal,
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.message || 'Unable to load payments and credits.');
        }

        const itemsKey = view === 'wallets'
          ? 'wallets'
          : view === 'transactions'
            ? 'transactions'
            : view === 'payments'
              ? 'payments'
              : view === 'issues'
                ? 'issues'
                : 'products';

        setListState({
          loading: false,
          refreshing: false,
          error: '',
          items: data[itemsKey] || [],
          pagination: data.pagination || {
            page: 1,
            total_pages: 1,
            total_count: 0,
          },
        });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setListState((current) => ({
          ...current,
          loading: false,
          refreshing: false,
          error: error.message || 'Unable to load payments and credits.',
          items: [],
        }));
      }
    };

    loadList({ background: refreshNonce > 0 });

    return () => controller.abort();
  }, [config.endpoint, environment, filters, listState.pagination.page, range, refreshNonce, token, view]);

  const summary = summaryState.summary;

  const openReconciliationPanel = (row, label) => {
    setFlashMessage({ type: '', text: '' });
    setReconcileState({
      open: true,
      paymentId: row.payment_id || row.id,
      label,
      note: row.reconciliation_note || '',
      canRerunCreditGrant: Boolean(row.can_rerun_credit_grant),
      rerunCreditGrant: false,
      submitting: false,
      error: '',
    });
  };

  const closeReconciliationPanel = () => {
    setReconcileState({
      open: false,
      paymentId: '',
      label: '',
      note: '',
      canRerunCreditGrant: false,
      rerunCreditGrant: false,
      submitting: false,
      error: '',
    });
  };

  const submitReconciliation = async () => {
    setReconcileState((current) => ({ ...current, submitting: true, error: '' }));
    setFlashMessage({ type: '', text: '' });

    try {
      const response = await fetch('/.netlify/functions/admin-reconcile-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          payment_id: reconcileState.paymentId,
          note: reconcileState.note,
          mark_reviewed: true,
          rerun_credit_grant: reconcileState.canRerunCreditGrant && reconcileState.rerunCreditGrant,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Unable to reconcile payment.');
      }

      setFlashMessage({
        type: 'success',
        text: data.payment?.credits_regranted > 0
          ? `Payment reviewed and ${data.payment.credits_regranted} missing credits were re-granted.`
          : 'Payment marked reviewed successfully.',
      });
      closeReconciliationPanel();
      triggerRefresh();
    } catch (error) {
      setReconcileState((current) => ({
        ...current,
        submitting: false,
        error: error.message || 'Unable to reconcile payment.',
      }));
    }
  };

  const filterBar = useMemo(() => {
    if (view === 'overview') {
      return null;
    }

    if (view === 'wallets') {
      return (
        <section className="cq-admin-panel cq-admin-payment-filters">
          <label className="cq-admin-field"><span>Search</span><input className="cq-admin-input" value={filters.query} onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))} /></label>
          <label className="cq-admin-field"><span>Internal/Test</span><select className="cq-admin-input" value={filters.internal_test} onChange={(event) => setFilters((current) => ({ ...current, internal_test: event.target.value }))}><option value="">All</option><option value="true">Yes</option><option value="false">No</option></select></label>
          <label className="cq-admin-field"><span>Has Purchases</span><select className="cq-admin-input" value={filters.has_purchases} onChange={(event) => setFilters((current) => ({ ...current, has_purchases: event.target.value }))}><option value="">All</option><option value="true">Yes</option><option value="false">No</option></select></label>
          <label className="cq-admin-field"><span>Manual Adjustments</span><select className="cq-admin-input" value={filters.has_manual_adjustments} onChange={(event) => setFilters((current) => ({ ...current, has_manual_adjustments: event.target.value }))}><option value="">All</option><option value="true">Yes</option><option value="false">No</option></select></label>
          <label className="cq-admin-field"><span>Min Balance</span><input className="cq-admin-input" type="number" value={filters.min_balance} onChange={(event) => setFilters((current) => ({ ...current, min_balance: event.target.value }))} /></label>
          <label className="cq-admin-field"><span>Max Balance</span><input className="cq-admin-input" type="number" value={filters.max_balance} onChange={(event) => setFilters((current) => ({ ...current, max_balance: event.target.value }))} /></label>
        </section>
      );
    }

    if (view === 'transactions') {
      return (
        <section className="cq-admin-panel cq-admin-payment-filters">
          <label className="cq-admin-field"><span>Search</span><input className="cq-admin-input" value={filters.query} onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))} /></label>
          <label className="cq-admin-field"><span>Transaction Type</span><select className="cq-admin-input" value={filters.transaction_type} onChange={(event) => setFilters((current) => ({ ...current, transaction_type: event.target.value }))}><option value="">All</option><option value="grant">Grant</option><option value="purchase">Purchase</option><option value="spend">Spend</option><option value="refund">Refund</option><option value="adjustment">Adjustment</option><option value="bonus">Bonus</option></select></label>
          <label className="cq-admin-field"><span>Manual</span><select className="cq-admin-input" value={filters.manual_only} onChange={(event) => setFilters((current) => ({ ...current, manual_only: event.target.value }))}><option value="">All</option><option value="true">Manual Only</option><option value="false">Non-Manual</option></select></label>
          <label className="cq-admin-field"><span>Has Session</span><select className="cq-admin-input" value={filters.has_game_session} onChange={(event) => setFilters((current) => ({ ...current, has_game_session: event.target.value }))}><option value="">All</option><option value="true">Yes</option><option value="false">No</option></select></label>
          <label className="cq-admin-field"><span>Has Stripe Ref</span><select className="cq-admin-input" value={filters.has_stripe_payment} onChange={(event) => setFilters((current) => ({ ...current, has_stripe_payment: event.target.value }))}><option value="">All</option><option value="true">Yes</option><option value="false">No</option></select></label>
        </section>
      );
    }

    if (view === 'payments') {
      return (
        <section className="cq-admin-panel cq-admin-payment-filters">
          <label className="cq-admin-field"><span>Search</span><input className="cq-admin-input" value={filters.query} onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))} /></label>
          <label className="cq-admin-field"><span>Status</span><select className="cq-admin-input" value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}><option value="">All</option><option value="pending">Pending</option><option value="completed">Completed</option><option value="failed">Failed</option><option value="expired">Expired</option><option value="refunded">Refunded</option><option value="ignored">Ignored</option></select></label>
          <label className="cq-admin-field"><span>Wallet Updated</span><select className="cq-admin-input" value={filters.wallet_updated} onChange={(event) => setFilters((current) => ({ ...current, wallet_updated: event.target.value }))}><option value="">All</option><option value="true">Yes</option><option value="false">No</option></select></label>
          <label className="cq-admin-field"><span>Webhook Received</span><select className="cq-admin-input" value={filters.webhook_received} onChange={(event) => setFilters((current) => ({ ...current, webhook_received: event.target.value }))}><option value="">All</option><option value="true">Yes</option><option value="false">No</option></select></label>
          <label className="cq-admin-field"><span>Product</span><input className="cq-admin-input" value={filters.product} onChange={(event) => setFilters((current) => ({ ...current, product: event.target.value }))} /></label>
        </section>
      );
    }

    if (view === 'issues') {
      return (
        <section className="cq-admin-panel cq-admin-payment-filters">
          <label className="cq-admin-field"><span>Search</span><input className="cq-admin-input" value={filters.query} onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))} /></label>
          <label className="cq-admin-field"><span>Issue Status</span><select className="cq-admin-input" value={filters.issue_status} onChange={(event) => setFilters((current) => ({ ...current, issue_status: event.target.value }))}><option value="">All</option><option value="unresolved">Unresolved</option><option value="resolved">Resolved</option></select></label>
          <label className="cq-admin-field"><span>Event Type</span><input className="cq-admin-input" value={filters.event_type} onChange={(event) => setFilters((current) => ({ ...current, event_type: event.target.value }))} /></label>
        </section>
      );
    }

    return (
      <section className="cq-admin-panel cq-admin-payment-filters">
        <label className="cq-admin-field"><span>Search</span><input className="cq-admin-input" value={filters.query} onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))} /></label>
        <label className="cq-admin-field"><span>Active</span><select className="cq-admin-input" value={filters.active} onChange={(event) => setFilters((current) => ({ ...current, active: event.target.value }))}><option value="">All</option><option value="true">Active</option><option value="false">Inactive</option></select></label>
      </section>
    );
  }, [filters, view]);

  return (
    <section className="cq-admin-page">
      <header className="cq-admin-page__header">
        <div>
          <p className="cq-admin-page__eyebrow">Payments & Credits Admin</p>
          <h2 className="cq-admin-page__title">{config.title}</h2>
          <p className="cq-admin-page__copy">Operational visibility into wallets, credits, payments, and reconciliation issues.</p>
        </div>
        <div className="cq-admin-page__actions">
          <div className="cq-admin-page__actions-row">
            <div className="cq-admin-range-filter" role="tablist" aria-label="Payments range">
              {RANGE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`cq-admin-range-filter__button${range === option.value ? ' is-active' : ''}`}
                  onClick={() => setRange(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="cq-admin-payment-environment-select">
              <select
                className="cq-admin-input"
                value={environment}
                onChange={(event) => setEnvironment(event.target.value)}
                aria-label="Environment"
              >
                {ENVIRONMENT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              disabled={summaryState.loading || summaryState.refreshing || listState.loading || listState.refreshing}
              onClick={triggerRefresh}
            >
              Refresh
            </button>
          </div>
          <p className="cq-admin-page__meta">
            {summaryState.refreshing || listState.refreshing
              ? 'Refreshing...'
              : `Last updated ${formatDateTime(summaryState.lastUpdatedAt)}`}
          </p>
        </div>
      </header>

      <nav className="cq-admin-session-tabs" aria-label="Payments navigation">
        {tabs.map((tab) => (
          <NavLink key={tab.key} to={tab.to} end={tab.end} className={({ isActive }) => `cq-admin-session-tab${isActive ? ' is-active' : ''}`}>
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {summaryState.loading ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">Loading payments and credits...</p></div> : null}
      {!summaryState.loading && summaryState.error ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">{summaryState.error}</p></div> : null}

      {!summaryState.loading && !summaryState.error && summary ? (
        <div className="cq-admin-metrics-grid">
          <SummaryCard title="Revenue" value={formatCurrency(summary.revenue_amount)} helper="Successful Stripe payments" />
          <SummaryCard title="Credits Purchased" value={formatInteger(summary.credits_purchased)} helper="Selected period" />
          <SummaryCard title="Credits Consumed" value={formatInteger(summary.credits_consumed)} helper="Selected period" />
          <SummaryCard title="Outstanding Credits" value={formatInteger(summary.outstanding_credits)} helper="Shared DB wallet total" />
          <SummaryCard title="Failed Payments" value={formatInteger(summary.failed_payments)} helper="Selected period" />
          <SummaryCard title="Manual Grants" value={formatInteger(summary.manual_credits_granted)} helper={`Removed: ${formatInteger(summary.manual_credits_removed)}`} />
        </div>
      ) : null}

      {filterBar}

      {flashMessage.text ? (
        <section className={`cq-admin-panel cq-admin-reconcile-flash cq-admin-reconcile-flash--${flashMessage.type || 'info'}`}>
          <p>{flashMessage.text}</p>
        </section>
      ) : null}

      {reconcileState.open ? (
        <section className="cq-admin-panel cq-admin-reconcile-panel">
          <div className="cq-admin-ops-section__header">
            <h3 className="cq-admin-section-title">Payment Reconciliation</h3>
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              onClick={closeReconciliationPanel}
              disabled={reconcileState.submitting}
            >
              Cancel
            </button>
          </div>
          <div className="cq-admin-reconcile-grid">
            <div>
              <p className="cq-admin-page__meta">Payment</p>
              <p>{reconcileState.label}</p>
            </div>
            <label className="cq-admin-field cq-admin-reconcile-note">
              <span>Reconciliation Note</span>
              <textarea
                className="cq-admin-input"
                value={reconcileState.note}
                onChange={(event) => setReconcileState((current) => ({ ...current, note: event.target.value }))}
                rows={4}
              />
            </label>
            {reconcileState.canRerunCreditGrant ? (
              <label className="cq-admin-checkbox">
                <input
                  type="checkbox"
                  checked={reconcileState.rerunCreditGrant}
                  onChange={(event) => setReconcileState((current) => ({ ...current, rerunCreditGrant: event.target.checked }))}
                />
                <span>Re-run missing credit grant for this payment</span>
              </label>
            ) : (
              <p className="cq-admin-page__meta">No credit replay is needed or eligible for this payment.</p>
            )}
          </div>
          {reconcileState.error ? <p className="cq-admin-inline-note cq-admin-inline-note--error">{reconcileState.error}</p> : null}
          <div className="cq-admin-reconcile-actions">
            <button
              type="button"
              className="cq-admin-action-button"
              onClick={submitReconciliation}
              disabled={reconcileState.submitting}
            >
              {reconcileState.submitting ? 'Saving...' : 'Save Reconciliation'}
            </button>
          </div>
        </section>
      ) : null}

      {view === 'wallets' ? (
        <p className="cq-admin-page__meta cq-admin-payments-note">
          Wallet balances are shared across this database. The environment filter only scopes supporting purchase and credit activity.
        </p>
      ) : null}

      {view === 'overview' ? (
        <section className="cq-admin-panel">
          <h3 className="cq-admin-section-title">Support Notes</h3>
          <ul className="cq-admin-summary-list">
            <li>Wallet corrections should still be performed from the user detail screen.</li>
            <li>Use Wallets to find balances, Credit Transactions to trace ledger movements, Stripe Payments to verify purchase handling, and Payment Issues to spot reconciliation failures.</li>
          </ul>
        </section>
      ) : null}

      {view !== 'overview' && listState.loading ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">Loading payments and credits...</p></div> : null}
      {view !== 'overview' && !listState.loading && listState.error ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">{listState.error}</p></div> : null}
      {view !== 'overview' && !listState.loading && !listState.error && !listState.items.length ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">No payment or credit activity found.</p></div> : null}

      {!listState.loading && !listState.error && view === 'wallets' && listState.items.length ? (
        <section className="cq-admin-panel">
          <div className="cq-admin-table-wrap">
            <table className="cq-admin-table">
              <thead>
                <tr>
                  <th>User Email</th>
                  <th>User ID</th>
                  <th>Balance</th>
                  <th>Lifetime Purchased</th>
                  <th>Lifetime Consumed</th>
                  <th>Manual Grants</th>
                  <th>Manual Removes</th>
                  <th>Last Purchase</th>
                  <th>Last Credit Usage</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {listState.items.map((row) => (
                  <tr key={row.user_id}>
                    <td>{row.email || 'Unknown'}</td>
                    <td className="cq-admin-cell-id">{row.user_id}</td>
                    <td>{row.credit_balance}</td>
                    <td>{row.lifetime_credits_purchased}</td>
                    <td>{row.lifetime_credits_consumed}</td>
                    <td>{row.manual_credits_granted}</td>
                    <td>{row.manual_credits_removed}</td>
                    <td>{formatDateTime(row.last_purchase_at)}</td>
                    <td>{formatDateTime(row.last_credit_usage_at)}</td>
                    <td><span className={paymentBadgeClass(row.status)}>{row.status}</span></td>
                    <td><Link className="cq-admin-card-link" to={`/admin/users/${row.user_id}`}>Open User</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {!listState.loading && !listState.error && view === 'transactions' && listState.items.length ? (
        <section className="cq-admin-panel">
          <div className="cq-admin-table-wrap">
            <table className="cq-admin-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>User</th>
                  <th>Type</th>
                  <th>Environment</th>
                  <th>Credit Amount</th>
                  <th>Balance After</th>
                  <th>Session</th>
                  <th>Stripe Ref</th>
                  <th>Admin</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {listState.items.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDateTime(row.created_at)}</td>
                    <td><Link className="cq-admin-card-link" to={`/admin/users/${row.user_id}`}>{row.user_email || row.user_id}</Link></td>
                    <td>{row.transaction_type}</td>
                    <td>{row.environment}</td>
                    <td className={row.credit_amount >= 0 ? 'cq-admin-payment-positive' : 'cq-admin-payment-negative'}>{row.credit_amount}</td>
                    <td>{row.balance_after}</td>
                    <td>{row.related_game_session_id ? <Link className="cq-admin-card-link cq-admin-cell-id" to={`/admin/sessions/${row.related_game_session_id}`}>{row.related_game_session_id}</Link> : 'None'}</td>
                    <td className="cq-admin-cell-id">{row.related_stripe_ref || 'None'}</td>
                    <td>{row.admin_email || 'Not manual'}</td>
                    <td>{row.reason || 'Not available'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {!listState.loading && !listState.error && view === 'payments' && listState.items.length ? (
        <section className="cq-admin-panel">
          <div className="cq-admin-table-wrap">
            <table className="cq-admin-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>User</th>
                  <th>Checkout</th>
                  <th>Payment Intent</th>
                  <th>Product</th>
                  <th>Environment</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Credits Expected</th>
                  <th>Credits Applied</th>
                  <th>Wallet Updated?</th>
                  <th>Webhook?</th>
                  <th>Reviewed</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {listState.items.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDateTime(row.created_at)}</td>
                    <td><Link className="cq-admin-card-link" to={`/admin/users/${row.user_id}`}>{row.user_email || row.user_id}</Link></td>
                    <td className="cq-admin-cell-id">{row.checkout_session_id || 'None'}</td>
                    <td className="cq-admin-cell-id">{row.payment_intent_id || 'None'}</td>
                    <td>{row.product_name}</td>
                    <td>{row.environment}</td>
                    <td>{formatCurrency(row.amount_paid)}</td>
                    <td><span className={paymentBadgeClass(row.needs_review ? 'needs_review' : row.payment_status)}>{row.needs_review ? 'needs_review' : row.payment_status}</span></td>
                    <td>{row.credits_expected}</td>
                    <td>{row.credits_applied}</td>
                    <td>{boolLabel(row.wallet_updated)}</td>
                    <td>{boolLabel(row.webhook_received)}</td>
                    <td>{row.reviewed_at ? formatDateTime(row.reviewed_at) : 'No'}</td>
                    <td>
                      <button
                        type="button"
                        className="cq-admin-card-link cq-admin-card-link--button"
                        onClick={() => openReconciliationPanel(row, row.checkout_session_id || row.payment_intent_id || row.id)}
                      >
                        Reconcile
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {!listState.loading && !listState.error && view === 'issues' && listState.items.length ? (
        <section className="cq-admin-panel">
          <div className="cq-admin-table-wrap">
            <table className="cq-admin-table">
              <thead>
                <tr>
                  <th>Occurred</th>
                  <th>User</th>
                  <th>Type</th>
                  <th>Environment</th>
                  <th>Stripe Event</th>
                  <th>Stripe Object ID</th>
                  <th>Error Message</th>
                  <th>Error Code</th>
                  <th>HTTP Status</th>
                  <th>Resolved At</th>
                  <th>Reviewed</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {listState.items.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDateTime(row.occurred_at)}</td>
                    <td>{row.user_id ? <Link className="cq-admin-card-link" to={`/admin/users/${row.user_id}`}>{row.user_email || row.user_id}</Link> : 'Unknown'}</td>
                    <td><span className={paymentBadgeClass(row.issue_type)}>{row.issue_type}</span></td>
                    <td>{row.environment}</td>
                    <td>{row.stripe_event_type || 'Not available'}</td>
                    <td className="cq-admin-cell-id">{row.stripe_object_id || 'Not available'}</td>
                    <td>{row.error_message}</td>
                    <td>{row.error_code || 'None'}</td>
                    <td>{row.http_status || 'None'}</td>
                    <td>{formatDateTime(row.resolved_at)}</td>
                    <td>{row.reviewed_at ? formatDateTime(row.reviewed_at) : 'No'}</td>
                    <td>
                      {row.payment_id ? (
                        <button
                          type="button"
                          className="cq-admin-card-link cq-admin-card-link--button"
                          onClick={() => openReconciliationPanel(row, row.stripe_object_id || row.payment_id)}
                        >
                          Reconcile
                        </button>
                      ) : 'Manual only'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {!listState.loading && !listState.error && view === 'products' && listState.items.length ? (
        <section className="cq-admin-panel">
          <div className="cq-admin-table-wrap">
            <table className="cq-admin-table">
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>Description</th>
                  <th>Credits</th>
                  <th>Price</th>
                  <th>Currency</th>
                  <th>Prod Product ID</th>
                  <th>Prod Price ID</th>
                  <th>Staging Product ID</th>
                  <th>Staging Price ID</th>
                  <th>Active</th>
                  <th>Environment</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {listState.items.map((row) => (
                  <tr key={row.id}>
                    <td>{row.product_name}</td>
                    <td>{row.description || 'None'}</td>
                    <td>{row.credits_included ?? 'N/A'}</td>
                    <td>{formatCurrency(row.price)}</td>
                    <td>{row.currency}</td>
                    <td className="cq-admin-cell-id">{row.stripe_product_id || 'Not configured'}</td>
                    <td className="cq-admin-cell-id">{row.stripe_price_id || 'Not configured'}</td>
                    <td className="cq-admin-cell-id">{row.staging_stripe_product_id || 'Not configured'}</td>
                    <td className="cq-admin-cell-id">{row.staging_stripe_price_id || 'Not configured'}</td>
                    <td><span className={paymentBadgeClass(row.active ? 'active' : 'inactive')}>{row.active ? 'active' : 'inactive'}</span></td>
                    <td>{row.environment}</td>
                    <td>{formatDateTime(row.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {!listState.loading && !listState.error && view !== 'overview' ? (
        <div className="cq-admin-session-pagination">
          <button
            type="button"
            className="cq-admin-action-button cq-admin-action-button--secondary"
            disabled={listState.pagination.page <= 1}
            onClick={() => setListState((current) => ({ ...current, pagination: { ...current.pagination, page: current.pagination.page - 1 } }))}
          >
            Previous
          </button>
          <span>Page {listState.pagination.page} of {listState.pagination.total_pages}</span>
          <button
            type="button"
            className="cq-admin-action-button cq-admin-action-button--secondary"
            disabled={listState.pagination.page >= listState.pagination.total_pages}
            onClick={() => setListState((current) => ({ ...current, pagination: { ...current.pagination, page: current.pagination.page + 1 } }))}
          >
            Next
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default AdminPaymentsPage;
